import clsx from 'clsx'
import { CalendarIcon } from 'lucide-react'
import React, { type ComponentProps, type ReactNode, useEffect } from 'react'
import {
  type Control,
  Controller,
  type FieldError,
  type FieldValues,
  type Merge,
  type Path,
  type PathValue,
  type RegisterOptions,
  useController,
  type UseFormRegister,
  type UseFormWatch,
} from 'react-hook-form'

import {
  BorderedRadioButton,
  Checkbox,
  DateInput,
  InputBlock, NumberInput,
  NumberMask,
  PasswordInput,
  RadioButton,
  RadioGroup,
  Select,
  SolidAlertTriangleIcon,
  TelephoneInput,
  TextInput
} from 'slab'

import { MultiselectField } from './MultiselectField'
import { cn, getFormattedDate } from '../../../utils'
import { Button } from '../../ui/button'
import { Calendar } from '../../ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover'
import { Textarea } from '../../ui/textarea'

interface FormTextFieldProps<T extends FieldValues> {
  autoComplete?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  error?: FieldError;
  fieldId: Path<T>;
  fieldLabel: string | React.ReactNode;
  register: UseFormRegister<T>;
  validationRule?: Record<string, any>;
  className?: string;
}

/** The following components are using DS components */
export function FormTextFieldDS<T extends FieldValues> ({
  autoFocus,
  disabled,
  error,
  fieldId,
  fieldLabel,
  description,
  placeholder,
  register,
  validationRule,
  maxLength,
}: FormTextFieldProps<T> & {
  placeholder?: string;
  description?: string;
  maxLength?: number;
}) {
  return (
    <div className='w-full'>
      <TextInput
        autoFocus={autoFocus}
        error={error?.message}
        label={fieldLabel}
        description={description}
        placeholder={placeholder}
        disabled={disabled}
        maxLength={maxLength}
        {...register(fieldId, {
          ...validationRule,
        })}
      />
    </div>
  )
}

export function FormPasswordInputDS<T extends FieldValues> ({
  autoFocus,
  disabled,
  error,
  fieldId,
  fieldLabel,
  description,
  placeholder,
  register,
  validationRule,
}: FormTextFieldProps<T> & {
  placeholder?: string;
  description?: string;
}) {
  return (
    <div className='w-full'>
      <PasswordInput
        autoFocus={autoFocus}
        error={error?.message}
        label={fieldLabel}
        description={description}
        placeholder={placeholder}
        disabled={disabled}
        {...register(fieldId, {
          ...validationRule,
        })}
      />
    </div>
  )
}

export function FormNumberFieldDS<T extends FieldValues> ({
  disabled,
  error,
  description,
  fieldId,
  fieldLabel,
  leadingIcon,
  placeholder,
  step,
  register,
  validationRule,
  customWidth,
}: FormTextFieldProps<T> & {
  placeholder?: string;
  leadingIcon?: ComponentProps<typeof NumberInput>['leadingIcon'];
  description?: string;
  step?: string | number;
  customWidth?: string;
}) {
  return (
    <div className={clsx({ [customWidth || 'w-full']: true })}>
      <NumberInput
        error={error?.message}
        leadingIcon={leadingIcon}
        label={fieldLabel}
        description={description}
        placeholder={placeholder}
        step={step}
        disabled={disabled}
        {...register(fieldId, {
          ...validationRule,
        })}
      />
    </div>
  )
}

// This is for fields that have large numbers, such as income
// where it is helpful to have some formatting (i.e. commas)
// This converts the numeric value to a string, so use with caution
export function FormFormattedNumberFieldDS<T extends FieldValues> ({
  disabled,
  error,
  description,
  fieldId,
  fieldLabel,
  leadingIcon,
  placeholder,
  step,
  register,
  validationRule,
}: FormTextFieldProps<T> & {
  placeholder?: string;
  leadingIcon?: ComponentProps<typeof TextInput>['leadingIcon'];
  description?: string;
  step?: string | number;
}) {
  return (
    <div className='w-full'>
      <NumberMask>
        <TextInput
          error={error?.message}
          leadingIcon={leadingIcon}
          label={fieldLabel}
          description={description}
          placeholder={placeholder}
          step={step}
          disabled={disabled}
          {...register(fieldId, {
            ...validationRule,
          })}
        />
      </NumberMask>
    </div>
  )
}

const massageError = (error: string | undefined) => {
  if (error === 'is invalid') {
    return 'not a valid phone number'
  }
  return error
}

export function FormTelephoneInputDS<T extends FieldValues> ({
  disabled,
  error,
  fieldId,
  fieldLabel,
  placeholder,
  register,
  validationRule,
  customWidth,
}: FormTextFieldProps<T> & {
  placeholder?: string;
  customWidth?: string;
}) {
  return (
    <div className={clsx({ [customWidth || 'w-full']: true })}>
      <TelephoneInput
        error={massageError(error?.message)}
        label={fieldLabel}
        placeholder={placeholder}
        disabled={disabled}
        {...register(fieldId, {
          ...validationRule,
        })}
      />
    </div>
  )
}

export function FormDateFieldDS<T extends FieldValues> ({
  disabled,
  error,
  fieldId,
  fieldLabel,
  placeholder,
  min,
  max,
  register,
  withPortal,
  validationRule,
  onBlur,
}: FormTextFieldProps<T> & {
  placeholder?: string;
  min?: string;
  max?: string;
  withPortal?: boolean;
  onBlur?: (() => void);
}) {
  return (
    <div className='w-full'>
      <DateInput
        error={error?.message}
        label={fieldLabel}
        placeholder={placeholder}
        min={min}
        max={max}
        disabled={disabled}
        withPortal={withPortal}
        {...register(fieldId, {
          ...validationRule,
        })}
        onBlur={onBlur}
      />
    </div>
  )
}

export function FormRadioGroupDS<T extends FieldValues> ({
  control,
  name,
  label,
  options,
  disabled,
  rules,
  layout,
  buttonVariant = 'default',
  buttonSize,
  description,
}: {
  control: Control<T>;
  name: Path<T>;
  label: string;
  options: Array<{
    label: string | ReactNode;
    value: string;
    disabled?: boolean;
  }>;
  disabled?: boolean;
  rules?: Omit<
    RegisterOptions<T, Path<T>>,
    'disabled' | 'valueAsNumber' | 'valueAsDate' | 'setValueAs'
  >;
  layout?: 'horizontal' | 'vertical';
  buttonVariant?: 'default' | 'bordered';
  buttonSize?: 'small' | 'large';
  description?: React.ReactNode;
}) {
  const {
    field: { value, onChange },
    fieldState: { error },
  } = useController({ name, control, rules })

  return (
    <div className='w-full'>
      <RadioGroup label={label} disabled={disabled} name={name} layout={layout} description={description}>
        {options.map((option) => {
          const commonProps = {
            label: option.label,
            value: option.value,
            disabled: option.disabled || disabled,
            checked: value === option.value,
            onChange: () => onChange(option.value),
          }

          if (buttonVariant === 'bordered') {
            return (
              <BorderedRadioButton
                key={option.value}
                {...commonProps}
                size={buttonSize}
              />
            )
          }

          return <RadioButton key={option.value} {...commonProps} />
        })}
      </RadioGroup>
      {error && (
        <div className='flex gap-x-1 mt-1'>
          <div className='text-red-400'>
            <SolidAlertTriangleIcon size={16} />
          </div>
          <div className='text-xs text-red-700 leading-normal font-semibold'>
            {error.message}
          </div>
        </div>
      )}
    </div>
  )
}

export function FormCheckboxFieldDS<T extends FieldValues> ({
  disabled,
  error,
  fieldId,
  fieldLabel,
  register,
  validationRule,
}: {
  disabled?: boolean;
  error?: FieldError;
  fieldId: Path<T>;
  fieldLabel: string | React.ReactNode;
  register: UseFormRegister<T>;
  validationRule?: Record<string, any>;
}) {
  return (
    <Checkbox
      label={fieldLabel}
      disabled={disabled}
      error={error?.message}
      {...register(fieldId, {
        ...validationRule,
      })}
    />
  )
}

export type FieldOption = {
  label: string | number;
  value: string | number;
  disabled?: boolean;
}

export type FormSelectFieldDSProps<T extends FieldValues> = {
  disabled?: boolean;
  error?: FieldError;
  fieldId: Path<T>;
  fieldLabel: string;
  fieldOptions: FieldOption[];
  placeholder?: string;
  register: UseFormRegister<T>;
  description?: string | React.ReactNode;
  validationRule?: Record<
    string,
    string | ((value: string) => string | undefined)
  >;
}

export function FormSelectFieldDS<T extends FieldValues> ({
  disabled,
  error,
  fieldId,
  fieldLabel,
  fieldOptions,
  placeholder,
  register,
  validationRule,
  description,
}: FormSelectFieldDSProps<T>) {
  return (
    <Select
      label={fieldLabel}
      disabled={disabled}
      error={error?.message}
      placeholder={placeholder}
      description={description}
      {...register(fieldId, {
        ...validationRule,
      })}
    >
      {fieldOptions.map((option) => (
        <option
          key={option.value}
          value={option.value}
          disabled={option.disabled}
        >
          {option.label}
        </option>
      ))}
    </Select>
  )
}

/** The field components below are not using DS components, will be replaced by above */
export function FormTextField<T extends FieldValues> ({
  autoComplete,
  autoFocus,
  disabled,
  error,
  fieldId,
  fieldLabel,
  register,
  validationRule,
}: FormTextFieldProps<T>) {
  return (
    <div
      className={clsx({
        'flex flex-col w-full': true,
        'field-with-errors': !!error,
      })}
    >
      <label
        className='label-default font-medium text-ds-text-secondary mb-2'
        htmlFor={fieldId}
      >
        {fieldLabel}
      </label>
      <input
        id={fieldId}
        disabled={disabled}
        className='input-default'
        aria-invalid={error ? 'true' : 'false'}
        {...register(fieldId, {
          ...validationRule,
        })}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
      />
      {error && <span className='field-error'>{error.message}</span>}
    </div>
  )
}

export function FormTextArea<T extends FieldValues> ({
  autoComplete,
  disabled,
  error,
  fieldId,
  fieldLabel,
  register,
  validationRule,
  className,
}: FormTextFieldProps<T>) {
  return (
    <div
      className={clsx({
        'flex flex-col w-full': true,
        'field-with-errors': !!error,
      })}
    >
      <label
        className='label-default font-medium text-ds-text-secondary mb-2'
        htmlFor={fieldId}
      >
        {fieldLabel}
      </label>
      <Textarea
        id={fieldId}
        disabled={disabled}
        className={clsx({
          'input-default': true,
          [className || '']: true,
        })}
        aria-invalid={error ? 'true' : 'false'}
        data-testid={`textarea-${fieldId}`}
        {...register(fieldId, {
          ...validationRule,
        })}
        autoComplete={autoComplete}
      />
      {error && <span className='field-error'>{error.message}</span>}
    </div>
  )
}

export function FormIntegerField<T extends FieldValues> ({
  autoComplete,
  disabled,
  error,
  fieldId,
  fieldLabel,
  register,
  validationRule,
  placeholder,
}: {
  autoComplete?: string;
  disabled?: boolean;
  error?: FieldError;
  fieldId: Path<T>;
  fieldLabel: string;
  register: UseFormRegister<T>;
  validationRule?: Record<string, string>;
  placeholder?: string;
}) {
  return (
    <div
      className={clsx({
        'flex flex-col w-full': true,
        'field-with-errors': !!error,
      })}
    >
      <label
        className='label-default font-medium text-ds-text-secondary mb-2'
        htmlFor={fieldId}
      >
        {fieldLabel}
      </label>
      <input
        type='number'
        id={fieldId}
        disabled={disabled}
        className='input-default'
        placeholder={placeholder}
        aria-invalid={error ? 'true' : 'false'}
        {...register(fieldId, {
          ...validationRule,
          valueAsNumber: true,
          validate: (value) =>
            Number.isInteger(value) || 'Value must be an integer',
        })}
        autoComplete={autoComplete}
      />
      {error && <span className='field-error'>{error.message}</span>}
    </div>
  )
}

export function FormSelectField<T extends FieldValues> ({
  disabled,
  autoFocus,
  error,
  fieldId,
  fieldLabel,
  fieldOptions,
  register,
  validationRule,
  labelIsHeader = true
}: {
  disabled?: boolean;
  autoFocus?: boolean;
  error?: FieldError;
  fieldId: Path<T>;
  fieldLabel: string | ReactNode;
  fieldOptions: Array<FieldOption>;
  register: UseFormRegister<T>;
  validationRule?: Record<
    string,
    string | ((value: string) => string | undefined)
  >;
  labelIsHeader?: boolean;
}) {
  return (
    <div
      className={clsx({
        'flex flex-col w-full': true,
        'field-with-errors': !!error,
      })}
    >
      {labelIsHeader
        ? (
          <label
            className='label-default font-medium text-ds-text-secondary mb-2'
            htmlFor={fieldId}
          >
            {fieldLabel}
          </label>
          )
        : (
          <legend className='font-semibold text-xs text-gray-800 mb-2'>{fieldLabel}</legend>
          )}
      <select
        id={fieldId}
        disabled={disabled}
        autoFocus={autoFocus}
        className='input-default'
        aria-invalid={error ? 'true' : 'false'}
        data-testid={`select-${fieldId}`}
        {...register(fieldId, {
          ...validationRule,
        })}
      >
        {fieldOptions.map((option) => (
          <option
            key={option.value}
            value={option.value}
            disabled={option.disabled}
          >
            {option.label}
          </option>
        ))}
      </select>
      {error && <span className='field-error'>{error.message}</span>}
    </div>
  )
}

export function FormMultiselectField<T extends FieldValues> ({
  control,
  error,
  fieldId,
  fieldLabel,
  fieldOptions,
}: {
  control: Control<T>;
  error?: Merge<FieldError, (FieldError | undefined)[]>;
  fieldId: Path<T>;
  fieldLabel: string;
  fieldOptions: Array<Record<'label' | 'value', string>>;
}) {
  return (
    <Controller
      control={control}
      name={fieldId}
      render={({ field: { onChange, value } }) => (
        <div
          className={clsx({
            'flex flex-col w-full': true,
            'field-with-errors': !!error,
          })}
        >
          <label
            className='label-default font-medium text-ds-text-secondary mb-2'
            htmlFor={fieldId}
          >
            {fieldLabel}
          </label>
          <MultiselectField
            options={fieldOptions}
            handleChange={onChange}
            value={value}
          />
          {error && <span className='field-error'>{error.message}</span>}
        </div>
      )}
    />
  )
}

export function FormDateField<T extends FieldValues> ({
  disabled,
  error,
  fieldId,
  fieldLabel,
  register,
  validationRule,
  placeholder,
}: {
  disabled?: boolean;
  error?: FieldError;
  fieldId: Path<T>;
  fieldLabel: string;
  register: UseFormRegister<T>;
  validationRule?: Record<
    string,
    string | ((value: string) => string | undefined)
  >;
  placeholder?: string;
}) {
  return (
    <div
      className={clsx({
        'flex flex-col w-full': true,
        'field-with-errors': !!error,
      })}
    >
      <label
        className='label-default font-medium text-ds-text-secondary mb-2'
        htmlFor={fieldId}
      >
        {fieldLabel}
      </label>
      <input
        type='date'
        id={fieldId}
        disabled={disabled}
        className='input-default'
        placeholder={placeholder}
        aria-invalid={error ? 'true' : 'false'}
        {...register(fieldId, {
          ...validationRule,
        })}
      />
      {error && <span className='field-error'>{error.message}</span>}
    </div>
  )
}

export function FormDatePickerField<T extends FieldValues> ({
  error,
  fieldId,
  fieldLabel,
  watch,
  // Callback to handle date selection. Its expected you'll want to call setValue for the associated fieldId;
  // however, you can choose to store the data as a string or Date object based on the needs of your form.
  // Its assumed you'll call setValue() within this callback from the scope of your form in the parent component.
  handleSelect,
  // Callback to specify which dates are valid. By default, all dates are valid. Return a range of valid dates
  // if you want to restrict selection.
  // Example: (date) => date < new Date() (e.g. disables all dates in the past)
  disabledCallback = () => false,
  // Inputs need to be converted to Date objects for the calendar to work. If the form field is a Date, no conversion is needed.
  // Otherwise, use this callback to convert the form field value to a Date object.
  selectedDateConversionFn = (selectedDate) => selectedDate,
  timezone = 'UTC',
}: {
  disabled?: boolean;
  error?: FieldError;
  fieldId: Path<T>;
  fieldLabel: string;
  watch: UseFormWatch<T>;
  handleSelect: (date: Date | undefined) => void;
  disabledCallback?: (date: Date) => boolean;
  selectedDateConversionFn?: (selectedDate: PathValue<T, Path<T>>) => Date;
  timezone?: Intl.DateTimeFormatOptions['timeZone'];
}) {
  const selectedDate = watch(fieldId)

  return (
    <div
      className={clsx({
        'flex flex-col w-full': true,
        'field-with-errors': !!error,
      })}
    >
      <label
        className='label-default font-medium text-ds-text-secondary mb-2'
        htmlFor={fieldId}
      >
        {fieldLabel}
      </label>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant='form'
            className={cn(
              'w-auto pl-3 text-left font-normal rounded-md',
              !selectedDate && 'text-muted-foreground'
            )}
            data-testid='calendar-trigger'
          >
            {selectedDate
              ? (
                  getFormattedDate(selectedDate, { timeZone: timezone })
                )
              : (
                <span>Pick a date</span>
                )}
            <CalendarIcon className='ml-auto h-4 w-4 opacity-50' />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className='w-auto p-0 pointer-events-auto'
          align='start'
        >
          <Calendar
            mode='single'
            selected={selectedDateConversionFn(selectedDate)}
            onSelect={handleSelect}
            disabled={disabledCallback}
            autoFocus
            defaultMonth={selectedDateConversionFn(selectedDate) || new Date()}
          />
        </PopoverContent>
      </Popover>
      {error && <span className='field-error'>{error.message}</span>}
    </div>
  )
}

export function FormDateInputField<T extends FieldValues> ({
  disabled,
  disablePopover,
  serverError,
  fieldId,
  fieldLabel,
  watch,
  onSelectDate,
  minDate,
  maxDate,
  disabledDateFilter,
  errorMessages = {
    minDate: 'Date is too far back in the past',
    maxDate: 'Date is too far in the future',
  },
}: {
  disabled?: boolean;
  disablePopover?: boolean;
  serverError?: FieldError;
  fieldId: Path<T>;
  fieldLabel: ReactNode;
  watch: UseFormWatch<T>;
  onSelectDate: (date: Date | undefined) => void;
  minDate?: Date;
  maxDate?: Date;
  disabledDateFilter?: (date: Date) => boolean;
  errorMessages?: {
    minDate: string;
    maxDate: string;
  };
}) {
  const selectedDateFromForm = watch(fieldId)
  const [inputValue, setInputValue] = React.useState('')
  const [selectedDate, setSelectedDate] = React.useState<Date | undefined>(
    undefined
  )
  const [month, setMonth] = React.useState(new Date())
  const [frontendError, setFrontendError] = React.useState<
    FieldError | undefined
  >(undefined)

  useEffect(() => {
    // If the date is already set in the form, populate the input field with this value.
    if (selectedDateFromForm) {
      const [yyyy, mm, dd] = selectedDateFromForm.split('-')
      setInputValue(`${mm}/${dd}/${yyyy}`)
    }
  }, [selectedDateFromForm])

  // format MM/DD/YYYY
  const regex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/(\d{4})$/

  React.useEffect(() => {
    if (!frontendError) {
      if (selectedDate && minDate && selectedDate < minDate) {
        setFrontendError({
          type: 'frontend',
          message: errorMessages.minDate,
        })
      }

      if (selectedDate && maxDate && selectedDate > maxDate) {
        setFrontendError({
          type: 'frontend',
          message: errorMessages.maxDate,
        })
      }

      if (selectedDate && !regex.test(inputValue)) {
        setFrontendError({
          type: 'frontend',
          message: 'Invalid date provided',
        })
      }
    } else {
      let resetError = true

      if (selectedDate && maxDate && selectedDate > maxDate) {
        resetError = false
      }

      if (selectedDate && minDate && selectedDate < minDate) {
        resetError = false
      }

      if (resetError) {
        setFrontendError(undefined)
      }
    }
  }, [minDate, maxDate, selectedDate])

  // if the value is set by the input field
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const re = /^[0-9/]+$/
    let value = e.target.value

    if (inputValue.length > value.length) {
      // if the user has hit backspace (i.e. removed some characters), don't do any masking logic and just accept the updated input
      setInputValue(value)
      setSelectedDate(undefined)
      onSelectDate(undefined)
      setMonth(new Date())
    } else if (e.target.value === '' || re.test(e.target.value)) {
      // coerce input into the format MM/DD/YYYY

      // Remove existing slashes for processing
      value = value.replace(/\//g, '')
      if (value.length > 2) {
        value = value.slice(0, 2) + '/' + value.slice(2)
      }
      if (value.length > 5) {
        value = value.slice(0, 5) + '/' + value.slice(5)
      }

      // Limit length to 10 characters
      if (value.length > 10) {
        value = value.slice(0, 10)
      }

      setInputValue(value)

      // when the date is done, run selection logic
      if (value.length === 10) {
        const [mm, dd, yyyy] = value.split('/')
        const parsed = new Date(parseInt(yyyy), parseInt(mm) - 1, parseInt(dd))
        setSelectedDate(parsed)
        onSelectDate(parsed)
        setMonth(parsed)
      }
    }
  }

  // if the value is set by the day picker
  const handleDayPickerSelect = (date: Date | undefined) => {
    if (date) {
      setSelectedDate(date)
      onSelectDate(date)
      setInputValue(
        `${String(date.getMonth() + 1).padStart(2, '0')}/${String(
          date.getDate()
        ).padStart(2, '0')}/${date.getFullYear()}`
      )
    }
  }

  const error = serverError || frontendError

  return (
    <div
      className={clsx({
        'flex flex-col w-full': true,
        'field-with-errors': !!error,
      })}
    >
      <label
        className='label-default font-medium text-ds-text-secondary mb-2'
        htmlFor={fieldId}
      >
        {fieldLabel}
      </label>
      <Popover>
        {/* to support the calendar icon popover, we're going to style the input's container like an input and the input itself will not be styled */}
        <div className='input-overwrite w-fit flex pl-1 text-left font-normal border border-gray-200 rounded-md text-muted-foreground'>
          <input
            className='appearance-none border-none w-32 focus:border-none focus:outline-none focus:ring-0'
            placeholder='mm/dd/yyyy'
            type='text'
            value={inputValue}
            disabled={disabled}
            onChange={handleInputChange}
            id={fieldId}
          />
          {!disablePopover && (
            <PopoverTrigger asChild>
              <CalendarIcon
                role='icon'
                className='my-auto -ml-2 mr-3 h-4 opacity-50'
                data-testid='calendar-trigger'
              />
            </PopoverTrigger>
          )}
        </div>
        <PopoverContent className='w-auto p-0' align='end'>
          <Calendar
            month={month}
            onMonthChange={setMonth}
            mode='single'
            selected={selectedDate}
            onSelect={handleDayPickerSelect}
            disabled={disabledDateFilter}
          />
        </PopoverContent>
      </Popover>
      {error && <span className='field-error'>{error.message}</span>}
    </div>
  )
}

export function FormCheckboxField<T extends FieldValues> ({
  disabled,
  error,
  fieldId,
  fieldLabel,
  register,
  validationRule,
}: {
  disabled?: boolean;
  error?: FieldError;
  fieldId: Path<T>;
  fieldLabel: string;
  register: UseFormRegister<T>;
  validationRule?: Record<string, any>;
}) {
  return (
    <div
      className={clsx({
        'flex flex-row items-center my-2 gap-2': true,
        'field-with-errors': !!error,
      })}
    >
      <label
        className='label-default font-medium text-ds-text-secondary'
        htmlFor={fieldId}
      >
        {fieldLabel}
      </label>
      <input
        type='checkbox'
        id={fieldId}
        disabled={disabled}
        className='h-4 w-4 rounded border-gray-300 text-thatch-purple focus:ring-thatch-purple disabled:bg-slate-100'
        aria-invalid={error ? 'true' : 'false'}
        {...register(fieldId, {
          ...validationRule,
          setValueAs: (value) => value === true,
        })}
      />
      {error && <span className='field-error'>{error.message}</span>}
    </div>
  )
}

export function FormStateDropdownField<T extends FieldValues> ({
  disabled,
  error,
  fieldId,
  fieldLabel,
  placeholder,
  register,
  validationRule,
  spanNumber,
}: {
  disabled?: boolean;
  error?: FieldError;
  fieldId: Path<T>;
  fieldLabel: string;
  placeholder?: string;
  register: UseFormRegister<T>;
  validationRule?: Record<string, any>;
  spanNumber?: '1' | '2' | '3' | '4' | '5' | '6';
}) {
  return (
    <InputBlock.Select
      label={fieldLabel}
      placeholder={placeholder}
      span={spanNumber || '2'}
      error={error?.message}
      disabled={disabled}
      {...register(fieldId, validationRule)}
    >
      <option value=''>Select a state</option>
      <option value='AL'>Alabama</option>
      <option value='AK'>Alaska</option>
      <option value='AZ'>Arizona</option>
      <option value='AR'>Arkansas</option>
      <option value='CA'>California</option>
      <option value='CO'>Colorado</option>
      <option value='CT'>Connecticut</option>
      <option value='DE'>Delaware</option>
      <option value='DC'>District Of Columbia</option>
      <option value='FL'>Florida</option>
      <option value='GA'>Georgia</option>
      <option value='HI'>Hawaii</option>
      <option value='ID'>Idaho</option>
      <option value='IL'>Illinois</option>
      <option value='IN'>Indiana</option>
      <option value='IA'>Iowa</option>
      <option value='KS'>Kansas</option>
      <option value='KY'>Kentucky</option>
      <option value='LA'>Louisiana</option>
      <option value='ME'>Maine</option>
      <option value='MD'>Maryland</option>
      <option value='MA'>Massachusetts</option>
      <option value='MI'>Michigan</option>
      <option value='MN'>Minnesota</option>
      <option value='MS'>Mississippi</option>
      <option value='MO'>Missouri</option>
      <option value='MT'>Montana</option>
      <option value='NE'>Nebraska</option>
      <option value='NV'>Nevada</option>
      <option value='NH'>New Hampshire</option>
      <option value='NJ'>New Jersey</option>
      <option value='NM'>New Mexico</option>
      <option value='NY'>New York</option>
      <option value='NC'>North Carolina</option>
      <option value='ND'>North Dakota</option>
      <option value='OH'>Ohio</option>
      <option value='OK'>Oklahoma</option>
      <option value='OR'>Oregon</option>
      <option value='PA'>Pennsylvania</option>
      <option value='RI'>Rhode Island</option>
      <option value='SC'>South Carolina</option>
      <option value='SD'>South Dakota</option>
      <option value='TN'>Tennessee</option>
      <option value='TX'>Texas</option>
      <option value='UT'>Utah</option>
      <option value='VT'>Vermont</option>
      <option value='VA'>Virginia</option>
      <option value='WA'>Washington</option>
      <option value='WV'>West Virginia</option>
      <option value='WI'>Wisconsin</option>
      <option value='WY'>Wyoming</option>
    </InputBlock.Select>
  )
}
